/**
 * KITCHEN_DISPLAY and BAR_DISPLAY — one engine, two faces.
 *
 * A grill station and a cocktail bar run the identical process (offered →
 * accepted → preparing → completed → picked up), so there is ONE board here and
 * the difference is data: which station id it polls, which permission family it
 * acts under, and which modifier kinds it puts on the card (đá / đường / size /
 * topping for the bar).
 *
 * ---------------------------------------------------------------------------
 * DESIGNED FOR A HOT LINE, NOT A DESK
 * ---------------------------------------------------------------------------
 *  - Type is `kds-*` sized (18–52px) so a ticket is readable from 2–3 m.
 *  - Every control is `tap-xl` (88px). Gloved thumbs, wet hands, no stylus.
 *  - NOTHING is hover-only: hover does not exist on a wall-mounted touchscreen,
 *    so every action is a visible button on the card.
 *  - Allergens are rendered in solid danger colour at the top of the card, not
 *    as a subtle tag. A missed allergen is a hospitalisation.
 *  - The SLA ring is computed with `slaState` from @sonauto/domain against the
 *    SERVER clock (`board.serverTime`), never the device clock — a KDS screen
 *    with a drifted RTC would otherwise paint the whole board red.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { slaState } from '@sonauto/domain';
import { Badge, Button, EmptyState, ErrorState, LoadingState, Modal, Select, useConfirm, useToast } from '@sonauto/ui';
import { useApi, useAuth } from '../../auth/AuthProvider';
import { usePoll, useAsync, useNow, errorMessage } from '../../lib/hooks';
import { useRealtime } from '../../lib/realtime';
import { useOutbox } from '../../offline/OutboxProvider';
import { Chrome } from '../../components/Chrome';
import { useDeviceMode } from '../../device/DeviceModeProvider';
import { productionDomainFor } from '../../device/mode';
import type { DeviceMode } from '../../device/mode';
import { clock, readAllergenCodes, readModifierNames, spiceLabel } from '../../lib/format';
import type { KdsBoardResponse, KdsTicket, KdsTicketItem, ProductionStatusWire, StationsResponse } from '../../lib/types';

/** Board columns, in the order a cook reads them left to right. */
const COLUMNS: ReadonlyArray<{ id: string; label: string; statuses: readonly ProductionStatusWire[] }> = [
  { id: 'NEW', label: 'Mới', statuses: ['WAITING', 'OFFERED'] },
  { id: 'ACCEPTED', label: 'Đã nhận', statuses: ['ACCEPTED'] },
  { id: 'PREPARING', label: 'Đang làm', statuses: ['PREPARING', 'REMAKING'] },
  { id: 'BLOCKED', label: 'Tạm dừng / thiếu NL', statuses: ['PAUSED', 'WAITING_INGREDIENT', 'REMAKE_REQUIRED'] },
  { id: 'DONE', label: 'Hoàn thành chờ lấy', statuses: ['COMPLETED', 'WAITING_PICKUP'] },
];

const POLL_MS = 5_000;
/** With a live socket the poll is only a safety net, not the transport. */
const CONNECTED_POLL_MS = 60_000;

export interface ProductionShellProps {
  readonly mode: DeviceMode;
  readonly onActivateDevice: () => void;
}

export function ProductionShell({ mode, onActivateDevice }: ProductionShellProps): React.JSX.Element {
  const api = useApi();
  const { me } = useAuth();
  const device = useDeviceMode();
  const domain = productionDomainFor(mode);
  const branchId = me?.session.activeBranchId ?? null;

  const [stationId, setStationId] = useState<string>(device.stationId ?? '');

  // `GET /v1/stations` needs `branch.view`, which KITCHEN/BAR roles do NOT
  // hold. A 403 is expected; the station then has to come from the device
  // binding, which is why the picker degrades to a message instead of an error.
  const stations = useAsync<StationsResponse>(
    (signal) => api.org.stations({ branchId: branchId ?? '', activeOnly: true }, { signal }),
    [branchId],
    { enabled: branchId !== null },
  );

  // Realtime: subscribe to THIS station's queue. Events are advisory — every
  // one just schedules a refetch (debounced, so an 8-item ticket firing 8
  // events costs one request). When the socket cannot be established (no
  // `branch.view` for the token, or the connect route is absent) `connected`
  // stays false and the 5-second poll below remains the transport.
  const reloadRef = useRef<() => void>(() => undefined);
  const debounceRef = useRef<number | undefined>(undefined);
  const queueReload = useCallback(() => {
    if (debounceRef.current !== undefined) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => reloadRef.current(), 250);
  }, []);
  const realtime = useRealtime({
    api,
    branchId,
    topics: stationId === '' ? [] : [`kds:${stationId}`],
    enabled: branchId !== null && stationId !== '',
    onEvent: queueReload,
    onCatchUp: queueReload,
  });

  const board = usePoll<KdsBoardResponse>(
    (signal) => api.production.board(stationId, { signal }),
    realtime.connected ? CONNECTED_POLL_MS : POLL_MS,
    [stationId, realtime.connected],
    { enabled: stationId !== '' },
  );
  reloadRef.current = board.reload;

  const stationOptions = useMemo(
    () =>
      (stations.data?.stations ?? [])
        .filter((s) => (domain === 'bar' ? s.stationType === 'BAR' : s.stationType !== 'BAR'))
        .map((s) => ({ value: s.id, label: `${s.name} (${s.stationType})` })),
    [domain, stations.data],
  );

  return (
    <Chrome
      dark
      title={
        <span className="text-kds-sm font-bold">
          {domain === 'bar' ? 'Quầy pha chế' : 'Bếp'} · {board.data?.station.name ?? '—'}
        </span>
      }
      onActivateDevice={onActivateDevice}
      actions={
        <div className="flex items-center gap-2">
          <Badge tone={realtime.connected ? 'success' : 'neutral'} size="md">
            {realtime.connected ? 'Realtime' : 'Tự làm mới 5s'}
          </Badge>
          {board.data !== null ? (
            <>
              <Badge tone="danger" size="md">
                Trễ {board.data.counts.late}
              </Badge>
              <Badge tone="warning" size="md">
                Sắp trễ {board.data.counts.warning}
              </Badge>
              <Badge tone="neutral" size="md">
                Tổng {board.data.counts.total}
              </Badge>
            </>
          ) : null}
        </div>
      }
    >
      {stationId === '' ? (
        <div className="p-6">
          <Select
            value={stationId}
            onChange={(e) => {
              setStationId(e.target.value);
              device.setLocalMode(mode, e.target.value);
            }}
            placeholder="— Chọn station cho màn hình này —"
            options={stationOptions}
          />
          {stationOptions.length === 0 ? (
            <p className="text-flow mt-3 text-sm text-fg-muted">
              Không đọc được danh sách station (cần quyền <code className="font-mono">branch.view</code>, vai trò bếp
              / bar không có). Hãy gán station cho thiết bị này trong ứng dụng Quản trị, hoặc kích hoạt lại thiết bị
              kèm station.
            </p>
          ) : null}
        </div>
      ) : (
        <KdsBoard board={board.data} loading={board.initialLoading} error={board.error} onRetry={board.reload} domain={domain} />
      )}
    </Chrome>
  );
}

// ---------------------------------------------------------------------------

function KdsBoard({
  board,
  loading,
  error,
  onRetry,
  domain,
}: {
  readonly board: KdsBoardResponse | null;
  readonly loading: boolean;
  readonly error: Error | null;
  readonly onRetry: () => void;
  readonly domain: 'kitchen' | 'bar';
}): React.JSX.Element {
  // A local tick so the elapsed clock moves between polls; the SLA maths still
  // uses the server time as its origin.
  const now = useNow(1000);

  if (loading && board === null) return <LoadingState label="Đang tải bảng phiếu…" />;
  if (error !== null && board === null) return <ErrorState message={error.message} onRetry={onRetry} />;
  if (board === null) return <LoadingState />;

  // Drift between this device's clock and the server's, measured once per poll
  // and applied to every card. This is what makes an old tablet with a wrong
  // clock still paint the right colours.
  const skew = now - board.serverTime;

  const byColumn = COLUMNS.map((column) => ({
    ...column,
    tickets: board.tickets.filter((t) => column.statuses.includes(t.status)),
  }));

  return (
    <div className="grid h-full grid-cols-2 gap-2 overflow-x-auto p-2 xl:grid-cols-5">
      {byColumn.map((column) => (
        <section key={column.id} className="flex min-h-0 flex-col rounded-lg bg-surface-sunken">
          <header className="flex items-center justify-between px-3 py-2">
            <h2 className="text-kds-base font-bold text-fg">{column.label}</h2>
            <Badge tone="neutral" size="md">
              {column.tickets.length}
            </Badge>
          </header>
          <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-2">
            {column.tickets.length === 0 ? (
              <EmptyState title="Trống" description={undefined} />
            ) : (
              column.tickets.map((ticket) => (
                <TicketCard
                  key={ticket.id}
                  ticket={ticket}
                  now={now - skew}
                  warnRatioBp={board.station.slaWarningRatioBp}
                  domain={domain}
                  onChanged={onRetry}
                />
              ))
            )}
          </div>
        </section>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------

const RING_COLOUR: Record<'ON_TIME' | 'WARNING' | 'LATE', string> = {
  ON_TIME: 'stroke-success-solid',
  WARNING: 'stroke-warning-solid',
  LATE: 'stroke-danger-solid',
};

const CARD_EDGE: Record<'ON_TIME' | 'WARNING' | 'LATE', string> = {
  ON_TIME: 'ring-line',
  WARNING: 'ring-4 ring-warning-solid',
  LATE: 'ring-4 ring-danger-solid animate-pulse',
};

function TicketCard({
  ticket,
  now,
  warnRatioBp,
  domain,
  onChanged,
}: {
  readonly ticket: KdsTicket;
  readonly now: number;
  readonly warnRatioBp: number;
  readonly domain: 'kitchen' | 'bar';
  readonly onChanged: () => void;
}): React.JSX.Element {
  const api = useApi();
  const toast = useToast();
  const confirm = useConfirm();
  const outbox = useOutbox();
  const [detail, setDetail] = useState(false);

  // Recomputed locally rather than trusting the payload's snapshot, so the ring
  // keeps moving between five-second polls.
  const sla = slaState(now, ticket.sentAt ?? ticket.slaDueAt, ticket.slaDueAt, warnRatioBp);
  const allergens = readAllergenCodes(ticket.allergenFlags);

  const act = async (
    label: string,
    run: () => Promise<unknown>,
    offline?: { kind: 'PRODUCTION_ACCEPT' | 'PRODUCTION_START' | 'PRODUCTION_COMPLETE'; path: string },
  ): Promise<void> => {
    try {
      await run();
      onChanged();
    } catch (cause: unknown) {
      if (!outbox.snapshot.online && offline !== undefined) {
        await outbox.queue({
          kind: offline.kind,
          label: `${label} — phiếu ${ticket.ticketNo}`,
          request: { method: 'POST', path: offline.path, body: {} },
        });
        toast.warning('Đang ngoại tuyến', 'Thao tác đã lưu và sẽ gửi khi có mạng.');
        return;
      }
      toast.error(`Không ${label.toLowerCase()} được`, errorMessage(cause));
    }
  };

  return (
    <article className={`rounded-lg bg-surface p-3 shadow-card ring-1 ${CARD_EDGE[sla.state]}`}>
      <header className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-kds-lg font-black leading-tight text-fg">
            {ticket.tableId === null ? 'Mang về' : `Bàn ${ticket.tableId.slice(-4)}`}
          </p>
          <p className="text-kds-sm text-fg-muted">
            #{ticket.ticketNo} · vòng {ticket.roundNo} · {ticket.itemCount} món
          </p>
        </div>
        <SlaRing state={sla.state} elapsedRatioBp={sla.elapsedRatioBp} remainingSeconds={sla.remainingSeconds} />
      </header>

      {allergens.length > 0 ? (
        <p className="mt-2 rounded-md bg-danger-solid px-2 py-1 text-kds-base font-black uppercase tracking-wide text-white">
          ⚠ DỊ ỨNG: {allergens.join(' · ')}
        </p>
      ) : null}

      {ticket.note !== null && ticket.note !== '' ? (
        <p className="text-flow mt-2 rounded-md bg-warning-bg px-2 py-1 text-kds-sm font-semibold text-warning-fg">
          {ticket.note}
        </p>
      ) : null}

      <ul className="mt-2 space-y-2">
        {ticket.items.map((item) => (
          <ItemRow key={item.id} item={item} domain={domain} />
        ))}
      </ul>

      <footer className="mt-3 grid grid-cols-2 gap-2">
        {ticket.status === 'WAITING' || ticket.status === 'OFFERED' ? (
          <Button
            className="tap-xl col-span-2 text-kds-base"
            size="touch"
            onClick={() =>
              act('Nhận phiếu', () => api.production.acceptTicket(ticket.id), {
                kind: 'PRODUCTION_ACCEPT',
                path: `/v1/tickets/${ticket.id}/accept`,
              })
            }
          >
            NHẬN
          </Button>
        ) : null}
        {ticket.status === 'ACCEPTED' ? (
          <Button
            className="tap-xl col-span-2 text-kds-base"
            size="touch"
            onClick={() =>
              act('Bắt đầu', () => api.production.startTicket(ticket.id), {
                kind: 'PRODUCTION_START',
                path: `/v1/tickets/${ticket.id}/start`,
              })
            }
          >
            BẮT ĐẦU
          </Button>
        ) : null}
        {ticket.status === 'PREPARING' || ticket.status === 'REMAKING' ? (
          <Button
            className="tap-xl col-span-2 text-kds-base"
            size="touch"
            variant="success"
            onClick={() =>
              act('Hoàn thành', () => api.production.completeTicket(ticket.id), {
                kind: 'PRODUCTION_COMPLETE',
                path: `/v1/tickets/${ticket.id}/complete`,
              })
            }
          >
            XONG
          </Button>
        ) : null}
        <Button className="tap-xl" size="lg" variant="secondary" onClick={() => setDetail(true)}>
          Từng món
        </Button>
        <Button
          className="tap-xl"
          size="lg"
          variant="secondary"
          onClick={() => {
            void (async () => {
              const first = ticket.items[0];
              if (first === undefined) return;
              const reason = await confirm({
                title: 'Tạm dừng món',
                requireReason: true,
                reasonLabel: 'Lý do tạm dừng',
                reasonSuggestions: ['Chờ nguyên liệu', 'Hỏng thiết bị', 'Chờ món khác cùng bàn'],
                tone: 'danger',
              });
              if (reason === null) return;
              await act('Tạm dừng', () => api.production.pauseItem(first.id, reason));
            })();
          }}
        >
          Tạm dừng
        </Button>
      </footer>

      {detail ? (
        <ItemActionsModal ticket={ticket} domain={domain} onClose={() => setDetail(false)} onChanged={onChanged} />
      ) : null}
    </article>
  );
}

function ItemRow({ item, domain }: { readonly item: KdsTicketItem; readonly domain: 'kitchen' | 'bar' }): React.JSX.Element {
  const modifiers = readModifierNames(item.modifiers);
  return (
    <li className="rounded-md bg-surface-alt p-2">
      <div className="flex items-baseline gap-2">
        <span className="text-kds-xl font-black text-fg sa-tabular">{item.quantity}×</span>
        <span className="text-flow text-kds-base font-bold text-fg">{item.name}</span>
      </div>
      {modifiers.length > 0 ? (
        <p className="text-flow text-kds-sm font-semibold text-brand-700">
          {/* The bar cares about đá / đường / size / topping; the kitchen about
              cooking style. Both arrive in the same modifiers snapshot. */}
          {domain === 'bar' ? '🥤 ' : '• '}
          {modifiers.join(' · ')}
        </p>
      ) : null}
      {item.spiceLevel !== null && item.spiceLevel > 0 ? (
        <p className="text-kds-sm font-bold text-danger-fg">🌶 {spiceLabel(item.spiceLevel)}</p>
      ) : null}
      {item.allergenNote !== null && item.allergenNote !== '' ? (
        <p className="text-flow mt-1 rounded bg-danger-solid px-1 text-kds-sm font-bold text-white">
          ⚠ {item.allergenNote}
        </p>
      ) : null}
      {item.note !== null && item.note !== '' ? (
        <p className="text-flow text-kds-sm text-fg-muted">“{item.note}”</p>
      ) : null}
      {item.isRemake ? (
        <Badge tone="accent" size="md">
          LÀM LẠI (lần {item.remakeCount})
        </Badge>
      ) : null}
    </li>
  );
}

function SlaRing({
  state,
  elapsedRatioBp,
  remainingSeconds,
}: {
  readonly state: 'ON_TIME' | 'WARNING' | 'LATE';
  readonly elapsedRatioBp: number;
  readonly remainingSeconds: number;
}): React.JSX.Element {
  const radius = 26;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(1, Math.max(0, elapsedRatioBp / 10000));
  return (
    <div className="relative h-16 w-16 shrink-0">
      <svg viewBox="0 0 64 64" className="h-16 w-16 -rotate-90">
        <circle cx="32" cy="32" r={radius} className="stroke-line" strokeWidth="6" fill="none" />
        <circle
          cx="32"
          cy="32"
          r={radius}
          className={RING_COLOUR[state]}
          strokeWidth="6"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - progress)}
        />
      </svg>
      <span
        className={`absolute inset-0 flex items-center justify-center text-kds-sm font-black sa-tabular ${
          state === 'LATE' ? 'text-danger-fg' : state === 'WARNING' ? 'text-warning-fg' : 'text-fg'
        }`}
      >
        {clock(remainingSeconds)}
      </span>
    </div>
  );
}

/** Per-item actions: accept / start / complete / missing ingredient / remake. */
function ItemActionsModal({
  ticket,
  domain,
  onClose,
  onChanged,
}: {
  readonly ticket: KdsTicket;
  readonly domain: 'kitchen' | 'bar';
  readonly onClose: () => void;
  readonly onChanged: () => void;
}): React.JSX.Element {
  const api = useApi();
  const toast = useToast();
  const confirm = useConfirm();

  const run = async (label: string, fn: () => Promise<unknown>): Promise<void> => {
    try {
      await fn();
      onChanged();
    } catch (cause: unknown) {
      toast.error(`Không ${label} được`, errorMessage(cause));
    }
  };

  return (
    <Modal open onClose={onClose} title={`Phiếu ${ticket.ticketNo} — từng món`} size="lg">
      <ul className="space-y-3">
        {ticket.items.map((item) => (
          <li key={item.id} className="rounded-md bg-surface-alt p-3">
            <ItemRow item={item} domain={domain} />
            <div className="mt-2 flex flex-wrap gap-2">
              <Button className="tap-xl" onClick={() => void run('nhận', () => api.production.acceptItem(item.id))}>
                Nhận
              </Button>
              <Button className="tap-xl" onClick={() => void run('bắt đầu', () => api.production.startItem(item.id))}>
                Bắt đầu
              </Button>
              <Button
                className="tap-xl"
                variant="success"
                onClick={() => void run('hoàn thành', () => api.production.completeItem(item.id))}
              >
                Xong
              </Button>
              <Button
                className="tap-xl"
                variant="secondary"
                onClick={() => {
                  void (async () => {
                    const reason = await confirm({
                      title: `Thiếu nguyên liệu: ${item.name}`,
                      requireReason: true,
                      reasonLabel: 'Nguyên liệu thiếu / lý do',
                      tone: 'danger',
                    });
                    if (reason === null) return;
                    await run('báo thiếu nguyên liệu', () =>
                      api.production.missingIngredient(item.id, { ingredientNote: reason, requestRestock: true }, reason),
                    );
                  })();
                }}
              >
                Thiếu NL
              </Button>
              <Button
                className="tap-xl"
                variant="danger"
                onClick={() => {
                  void (async () => {
                    const reason = await confirm({
                      title: `Làm lại: ${item.name}`,
                      requireReason: true,
                      reasonLabel: 'Lý do làm lại',
                      reasonSuggestions: ['Sai món', 'Sai tuỳ chọn', 'Chất lượng', 'Nguội', 'Rơi vỡ'],
                      tone: 'danger',
                    });
                    if (reason === null) return;
                    await run('làm lại', () =>
                      api.production.remakeItem(item.id, { reasonCode: 'QUALITY', responsibleParty: 'KITCHEN' }, reason),
                    );
                  })();
                }}
              >
                Làm lại
              </Button>
            </div>
          </li>
        ))}
      </ul>
      <p className="text-flow mt-4 text-xs text-fg-subtle">
        Lưu ý hợp đồng API: món ở trạng thái <code className="font-mono">WAITING_INGREDIENT</code> hiện chưa có
        đường quay lại — <code className="font-mono">RESUME</code> chỉ hợp lệ từ <code className="font-mono">PAUSED</code>.
        Cần bổ sung sự kiện <code className="font-mono">INGREDIENT_RESOLVED</code> ở máy chủ.
      </p>
    </Modal>
  );
}
